import React from 'react'
import PropTypes from 'prop-types'
import ReactPlaceholder from 'react-placeholder'
import { TextRow, RoundShape } from 'react-placeholder/lib/placeholders'
import { withRouter } from 'react-router-dom'
import { connect } from 'react-redux'
import { omit, get } from 'lodash'
import queryString from 'query-string'
import styled from 'styled-components'
import { mediaQueries, SelectLayout } from '@blazar-plugins/device'
import { buildListUrl } from 'utils/url'
import { PlaceholderWrapper } from 'utils/LoadingAnimationStyles'
import { actionCreators } from 'reduxBits/quickass'
import FirstLevel from './FirstLevel'
import Loading from './Loading'
import SecondLevel from './SecondLevel'
import SideScroll from './SideScroll'

const Wrapper = styled.div`
  @media ${mediaQueries.large} {
    ul {
      white-space: normal;
    }
    border-bottom: 0;
    min-height: 90px;
  }
`

const NEW_CATEGORIES = {
  utbildning: true,
}

const FirstLevelWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 0;
  overflow: hidden;
`

const StyledFirstLevel = styled.div`
  @media ${mediaQueries.large} {
    margin: 0;
  }
  margin: 0 16px;
`

const iconStyles = { width: 50, height: 50 }

const Placeholder = React.memo(() => (
  <FirstLevelWrapper className="show-loading-animation">
    {Array.from(new Array(11)).map((_val, i) => (
      // eslint-disable-next-line react/no-array-index-key
      <StyledFirstLevel key={i}>
        <RoundShape color="#f9f7f6" style={iconStyles} />
        <TextRow color="#f9f7f6" />
      </StyledFirstLevel>
    ))}
  </FirstLevelWrapper>
))

function createCategoryProps(item, geoPref, searchParametersDefinition) {
  const apiQuery = queryString.parse(item.searchParameters)
  const link =
    item.url || buildListUrl(omit({ ...geoPref, ...apiQuery }, 'page'), searchParametersDefinition)
  const adCounter = typeof item.adCounter === 'undefined' ? null : item.adCounter

  return {
    link,
    adCounter,
    label: item.label,
    type: item.type,
    selected: item.selected || false,
    showNewBadge: Boolean(NEW_CATEGORIES[item.type]),
  }
}

export function buildItemProps(categories, geoPref, searchParametersDefinition) {
  return categories.map((item) => {
    return createCategoryProps(item, geoPref, searchParametersDefinition)
  })
}

function mapStateToProps(state) {
  const searchParametersDefinition = state.searchParams.byQueryKey
  const geoPref = state.geoPicker.geoPreference

  const { categories, breadcrumbs, isFetching, context } = state.quickass

  const items = searchParametersDefinition
    ? buildItemProps(categories, geoPref, searchParametersDefinition)
    : []

  return {
    items,
    isFetching,
    context,
    breadcrumbs,
    geoPref,
  }
}

function mapDispatchToProps(dispatch) {
  return {
    fetch: (query) => {
      dispatch(actionCreators.fetchQuickassCategories(query))
    },
  }
}

export class QuickassCategoryPicker extends React.Component {
  componentDidMount() {
    this.props.fetch(queryString.parse(this.props.location.search))
  }

  componentDidUpdate(prevProps) {
    if (this.props.location.search !== prevProps.location.search) {
      this.props.fetch(queryString.parse(this.props.location.search))
    }
  }

  isFirstLevel() {
    const context = queryString.parse(this.props.context)
    return !(get(context, 'q', false) || get(context, 'cg', false))
  }

  render() {
    const { isFetching, items, scrollBackground } = this.props

    if (!isFetching && items.length <= 0) {
      return null
    }

    let child
    if (this.isFirstLevel()) {
      child = (
        <PlaceholderWrapper>
          <ReactPlaceholder customPlaceholder={<Placeholder />} ready={!isFetching}>
            <FirstLevel {...this.props} />
          </ReactPlaceholder>
        </PlaceholderWrapper>
      )
    } else {
      child = isFetching ? <Loading /> : <SecondLevel {...this.props} />
    }

    return (
      <Wrapper data-cy="quick-ass">
        <SelectLayout
          layouts={{
            large: child,
            small: <SideScroll backgroundColor={scrollBackground}>{child}</SideScroll>,
          }}
        />
      </Wrapper>
    )
  }
}

QuickassCategoryPicker.propTypes = {
  items: PropTypes.array.isRequired,
  fetch: PropTypes.func.isRequired,
  isFetching: PropTypes.bool,
  context: PropTypes.string,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
  trackQuickAssCategoryClick: PropTypes.func,
  scrollBackground: PropTypes.string,
  breadcrumbs: PropTypes.array,
}

export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(QuickassCategoryPicker),
)
